import React from 'react'
import { ScrollReveal } from '../ui/scroll-reveal'
import { Network, Code, Users, Map, BarChart, ArrowDown, CheckCircle, Zap, Brain } from 'lucide-react'

// Define a custom ScrollReveal with reduced delay values
function ProcessScrollReveal({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  return (
    <ScrollReveal delay={delay * 0.15}>
      {children}
    </ScrollReveal>
  );
}

export function ProcessSection() {
  return (
    <section className="relative py-20 -mt-48 pt-64">
      <div className="container relative">
        <ScrollReveal>
          <div className="text-center space-y-4 max-w-3xl mx-auto mb-20">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              A Structured Approach That Builds Sustainable AI Capability
            </h2>
            <p className="text-lg text-muted-foreground font-light">
              Three phases that take you from understanding to implementation - delivering working AI systems and the knowledge to evolve them
            </p>
          </div>
        </ScrollReveal>

        {/* Timeline indicator */}
        <div className="hidden lg:flex absolute left-1/2 top-40 bottom-0 -ml-[1px] items-start">
          <div className="w-0.5 bg-gradient-to-b from-primary/30 via-primary/20 to-primary/10 h-[calc(100%-3rem)]"></div>
        </div>

        {/* Phase 1 */}
        <div className="relative pb-36">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <ProcessScrollReveal delay={0}>
              <div className="order-1">
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white mr-4 shadow-lg">
                    <span className="text-lg font-bold">1</span>
                  </div>
                  <h3 className="text-2xl font-bold tracking-tight text-primary">Phase 1</h3>
                </div>
    
                <div className="space-y-4">
                  <h3 className="text-2xl font-semibold tracking-tight">Discovery & Opportunity Assessment</h3>
                  <p className="text-muted-foreground text-lg">
                    Through structured interviews and working sessions with your teams, we capture the institutional knowledge that lives in people's heads - not just what's documented. We rapidly identify where AI creates the most value by understanding how experienced professionals actually make decisions.
                  </p>
                </div>
              </div>
            </ProcessScrollReveal>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 order-2">
              <ProcessScrollReveal delay={1}>
                <div className="border-2 border-primary/20 bg-primary/5 backdrop-blur-sm shadow-sm rounded-xl h-full hover:border-primary/40 transition-all">
                  <div className="p-5 md:p-6 text-center h-full flex flex-col">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto flex items-center justify-center mb-4">
                      <Users className="w-full h-full text-primary/70" strokeWidth={1.5} />
                    </div>
                    <h4 className="text-lg sm:text-xl font-medium mb-2">Expert Insights</h4>
                    <p className="text-sm sm:text-base text-muted-foreground">
                      We gather structured insights from key personnel across your organisation - identifying high-impact opportunities whether in operational workflows, project delivery, decision support or specialist functions.
                    </p>
                  </div>
                </div>
              </ProcessScrollReveal>
              
              <ProcessScrollReveal delay={1}>
                <div className="border-2 border-primary/20 bg-primary/5 backdrop-blur-sm shadow-sm rounded-xl h-full hover:border-primary/40 transition-all">
                  <div className="p-5 md:p-6 text-center h-full flex flex-col">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto flex items-center justify-center mb-4">
                      <Network className="w-full h-full text-primary/70" strokeWidth={1.5} />
                    </div>
                    <h4 className="text-lg sm:text-xl font-medium mb-2">Operational Analysis</h4>
                    <p className="text-sm sm:text-base text-muted-foreground">
                      We map current processes, identify knowledge-intensive activities and assess where AI augmentation creates the most value. This analysis establishes clear priorities and scope for Phase 2.
                    </p>
                  </div>
                </div>
              </ProcessScrollReveal>
            </div>
          </div>
          
          {/* Arrow indicator */}
          <div className="hidden lg:flex absolute -bottom-2 left-1/2 -translate-x-1/2 z-10">
            <div className="p-3 rounded-full bg-primary/10 shadow-md">
              <ArrowDown className="h-6 w-6 text-primary" />
            </div>
          </div>
        </div>

        {/* Phase 2 */}
        <div className="relative pb-36 mt-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <ProcessScrollReveal delay={0}>
              <div className="order-1 lg:order-2">
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white mr-4 shadow-lg">
                    <span className="text-lg font-bold">2</span>
                  </div>
                  <h3 className="text-2xl font-bold tracking-tight text-primary">Phase 2</h3>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-2xl font-semibold tracking-tight">Knowledge Architecture & Partner AI Development</h3>
                  <p className="text-muted-foreground text-lg">
                    We systematically structure your institutional knowledge for AI consumption and create your Partner AI. This isn't just documentation - we capture the unwritten rules, operational wisdom and domain expertise that makes experienced professionals effective.
                  </p>
                </div>
              </div>
            </ProcessScrollReveal>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 order-2 lg:order-1">
              <ProcessScrollReveal delay={1}>
                <div className="border-2 border-primary/20 bg-primary/5 backdrop-blur-sm shadow-sm rounded-xl h-full hover:border-primary/40 transition-all">
                  <div className="p-5 md:p-6 text-center h-full flex flex-col">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto flex items-center justify-center mb-4">
                      <Code className="w-full h-full text-primary/70" strokeWidth={1.5} />
                    </div>
                    <h4 className="text-lg sm:text-xl font-medium mb-2">Knowledge Architecture Development</h4>
                    <p className="text-sm sm:text-base text-muted-foreground">
                      We transform scattered knowledge - processes, terminology, decision frameworks, operational expertise - into structured, AI-ready systems. This Knowledge Architecture becomes a strategic asset that improves as AI models advance.
                    </p>
                  </div>
                </div>
              </ProcessScrollReveal>
              
              <ProcessScrollReveal delay={1}>
                <div className="border-2 border-primary/20 bg-primary/5 backdrop-blur-sm shadow-sm rounded-xl h-full hover:border-primary/40 transition-all">
                  <div className="p-5 md:p-6 text-center h-full flex flex-col">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto flex items-center justify-center mb-4">
                      <Brain className="w-full h-full text-primary/70" strokeWidth={1.5} />
                    </div>
                    <h4 className="text-lg sm:text-xl font-medium mb-2">Partner AI Creation</h4>
                    <p className="text-sm sm:text-base text-muted-foreground">
                      We create a custom AI assistant trained on your organisation's specific context. This isn't a generic tool - it's an AI system that understands your operations, speaks your language and applies your quality standards.
                    </p>
                  </div>
                </div>
              </ProcessScrollReveal>
            </div>
          </div>
          
          {/* Arrow indicator */}
          <div className="hidden lg:flex absolute -bottom-2 left-1/2 -translate-x-1/2 z-10">
            <div className="p-3 rounded-full bg-primary/10 shadow-md">
              <ArrowDown className="h-6 w-6 text-primary" />
            </div>
          </div>
        </div>

        {/* Phase 3 */}
        <div className="relative mt-16 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <ProcessScrollReveal delay={0}>
              <div className="order-1">
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white mr-4 shadow-lg">
                    <span className="text-lg font-bold">3</span>
                  </div>
                  <h3 className="text-2xl font-bold tracking-tight text-primary">Phase 3</h3>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-2xl font-semibold tracking-tight">Implementation & Strategic Roadmap</h3>
                  <p className="text-muted-foreground text-lg">
                    We work with your teams to validate AI capabilities through practical implementation, establish measurement frameworks and synthesise insights into a strategic roadmap for scaling AI across your organisation.
                  </p>
                </div>
              </div>
            </ProcessScrollReveal>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 order-2">
              <ProcessScrollReveal delay={1}>
                <div className="border-2 border-primary/20 bg-primary/5 backdrop-blur-sm shadow-sm rounded-xl h-full hover:border-primary/40 transition-all">
                  <div className="p-5 md:p-6 text-center h-full flex flex-col">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto flex items-center justify-center mb-4">
                      <BarChart className="w-full h-full text-primary/70" strokeWidth={1.5} />
                    </div>
                    <h4 className="text-lg sm:text-xl font-medium mb-2">Practical Implementation & Validation</h4>
                    <p className="text-sm sm:text-base text-muted-foreground">
                      Working demonstrations that prove value in your operational context. We establish clear metrics, validate integration approaches and demonstrate measurable impact.
                    </p>
                  </div>
                </div>
              </ProcessScrollReveal>
              
              <ProcessScrollReveal delay={1}>
                <div className="border-2 border-primary/20 bg-primary/5 backdrop-blur-sm shadow-sm rounded-xl h-full hover:border-primary/40 transition-all">
                  <div className="p-5 md:p-6 text-center h-full flex flex-col">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto flex items-center justify-center mb-4">
                      <Map className="w-full h-full text-primary/70" strokeWidth={1.5} />
                    </div>
                    <h4 className="text-lg sm:text-xl font-medium mb-2">Strategic Roadmap</h4>
                    <p className="text-sm sm:text-base text-muted-foreground">
                      We synthesise insights from all phases into a practical, phased roadmap aligned with your strategic objectives - clear priorities for expanding AI capabilities, with realistic milestones and resource requirements.
                    </p>
                  </div>
                </div>
              </ProcessScrollReveal>
            </div>
          </div>
          
          {/* End marker */}
          <div className="hidden lg:flex absolute bottom-0 left-1/2 -translate-x-1/2 z-10">
            <div className="p-3 rounded-full bg-primary/10 shadow-md">
              <CheckCircle className="h-6 w-6 text-primary" />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
